import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import type { OriginalsConfig, PlayEnvelope, PublicOutcome } from '../types.js';
import type { ScenePhase } from '../scenes/types.js';

export interface CardsLegendTier {
  multiplierBp: number;
  /** Unique rank labels of this tier's display cards (suit stripped), table order. */
  ranks: string[];
  /** Win chance in percent from the public outcome weights; null when weights are unusable. */
  chancePct: number | null;
}

interface PresenterTier {
  multiplierBp?: unknown;
  cards?: unknown;
}

/**
 * Merges the cards presenter tiers (which ranks represent each tier) with the
 * public outcome table (each tier's weight) into the rows the legend renders.
 * Sorted ascending by multiplier so it reads like the card ladder: low cards
 * lose, high cards pay progressively more.
 */
export function buildCardsLegend(
  presenterConfig: unknown,
  outcomes: PublicOutcome[],
): CardsLegendTier[] {
  const tiers =
    presenterConfig != null &&
    typeof presenterConfig === 'object' &&
    'tiers' in presenterConfig
      ? (presenterConfig as { tiers?: unknown }).tiers
      : undefined;
  if (!Array.isArray(tiers)) return [];

  const totalWeight = outcomes.reduce(
    (sum, o) => sum + (typeof o.weight === 'number' && o.weight > 0 ? o.weight : 0),
    0,
  );
  const weightOf = (bp: number): number =>
    outcomes
      .filter((o) => o.multiplierBp === bp && typeof o.weight === 'number')
      .reduce((sum, o) => sum + o.weight, 0);

  const rows: CardsLegendTier[] = [];
  for (const raw of tiers as PresenterTier[]) {
    if (raw == null || typeof raw !== 'object' || typeof raw.multiplierBp !== 'number') continue;
    const cards = Array.isArray(raw.cards) ? raw.cards : [];
    const ranks: string[] = [];
    for (const card of cards) {
      if (typeof card !== 'string' || card.length < 2) continue;
      const rank = card.slice(0, -1).toUpperCase();
      if (rank && !ranks.includes(rank)) ranks.push(rank);
    }
    if (ranks.length === 0) continue;
    const weight = weightOf(raw.multiplierBp);
    rows.push({
      multiplierBp: raw.multiplierBp,
      ranks,
      chancePct: totalWeight > 0 && weight > 0 ? (weight / totalWeight) * 100 : null,
    });
  }
  return rows.sort((a, b) => a.multiplierBp - b.multiplierBp);
}

/** Reads the tier the round actually landed on out of the play envelope. */
export function presentationMultiplierBp(presentation: unknown): number | null {
  if (presentation == null || typeof presentation !== 'object') return null;
  const bp = (presentation as { multiplierBp?: unknown }).multiplierBp;
  return typeof bp === 'number' ? bp : null;
}

export function formatBpMultiplier(multiplierBp: number): string {
  const x = multiplierBp / 10000;
  const text = Number.isInteger(x) ? String(x) : x.toFixed(2).replace(/\.?0+$/, '');
  return `${text}×`;
}

function formatPct(pct: number): string {
  return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
}

interface Props {
  config: OriginalsConfig;
  envelope: PlayEnvelope | null;
  phase: ScenePhase;
  /** Opens the full how-to-play guide dialog. */
  onHowTo: () => void;
}

/**
 * Payout ladder shown under the cards scene — the in-page "how to play": which
 * ranks lose, which ranks pay which multiplier, and how likely each tier is.
 * After a reveal the tier that hit lights up so the result is readable even
 * if the player missed the flip.
 */
export function CardsLegend({ config, envelope, phase, onHowTo }: Props) {
  const { t } = useTranslation('originals');
  const tiers = useMemo(
    () => buildCardsLegend(config.presenterConfig, config.outcomes),
    [config.presenterConfig, config.outcomes],
  );
  if (tiers.length === 0) return null;

  const hitBp =
    phase === 'revealed' && envelope
      ? presentationMultiplierBp(envelope.outcome.presentation)
      : null;

  return (
    <div className="mt-4 flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--text-muted)]">
          {t('cards.legend.title')}
        </span>
        <button
          type="button"
          onClick={onHowTo}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[12px] font-semibold text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)]"
        >
          <CircleHelp size={14} aria-hidden />
          {t('guide.open')}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {tiers.map((tier) => {
          const lose = tier.multiplierBp === 0;
          const hit = hitBp != null && hitBp === tier.multiplierBp;
          return (
            <div
              key={tier.multiplierBp}
              className={`flex flex-col gap-2 rounded-xl border p-2.5 transition-colors ${
                hit
                  ? lose
                    ? 'border-[var(--danger)]/60 bg-[var(--danger)]/10'
                    : 'border-[var(--primary)] bg-[var(--primary)]/12'
                  : 'border-[var(--border-subtle,var(--border))] bg-[var(--bg-elevated)]'
              }`}
            >
              <div className="flex flex-wrap gap-1">
                {tier.ranks.map((rank) => (
                  <span
                    key={rank}
                    className={`inline-flex h-7 min-w-[22px] items-center justify-center rounded-[5px] bg-[#f7f7f4] px-1 text-[12px] font-extrabold text-[#1a1a1a] shadow-[0_1px_2px_rgba(0,0,0,0.35)] ${
                      lose ? 'opacity-55' : ''
                    }`}
                  >
                    {rank}
                  </span>
                ))}
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={`text-[13px] font-extrabold bf-tnum ${
                    lose ? 'text-[var(--text-muted)]' : 'text-[var(--primary)]'
                  }`}
                >
                  {lose ? t('cards.legend.lose') : formatBpMultiplier(tier.multiplierBp)}
                </span>
                {tier.chancePct != null && (
                  <span className="text-[11px] font-semibold bf-tnum text-[var(--text-muted)]">
                    {formatPct(tier.chancePct)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
